import React from "react";
import {
  HOUSE_STATUS,
  MatchStatusEnum,
  PRIVATE_USE,
  SHARED_USE,
  TYPE_ROOM
} from "utils/constants";

export interface ICognitoAttributes {
  email: string;
  email_verified: boolean;
  locale: string;
  profile: RoleUser;
  sub: string; // user id
}

export interface ICognitoUser {
  id: string;
  email: string;
  email_verified: boolean;
  locale: string;
  role: RoleUser;
}

export type NonNullableProperties<T> = {
  [K in keyof T]: Exclude<T[K], null>;
};

export type RoleUser = "student" | "host";

export type THouseType = (typeof TYPE_ROOM)[number];
export const GenderValues = [
  "male",
  "female",
  "other",
  "prefer_not_answer"
] as const;
export const GenderValuesBE = ["male", "female", "any"] as const;

export type TGender = (typeof GenderValues)[number];
export type TGenderBE = (typeof GenderValuesBE)[number];
export type TVerify = "verifying" | "verified" | "unverified" | "failed";

export type TParamsQuery = { id: string };

export interface ISeo {
  title?: string;
  description?: string;
  keywords?: string;
  canonicallink?: string;
}

export interface IHousePhoto {
  file_name: string;
  url: string;
  timestamp: string;
  order: number;
}

export interface IEmail {
  type: "contact_form" | "newsletter" | "contact_me" | "partner";
  first_name?: string;
  last_name?: string;
  question?: string;
  phone?: string;
  category?: string;
  browser_language: string;
}

export interface IResponseShort {
  message: string;
  success: boolean;
}

export interface IUploadAvatarResponse extends IResponseShort {
  thumbnail_url?: string;
}

export interface ICityOfResidence {
  country: string;
  province: string;
  city: string;
}

export interface IHelpWidget {
  title: string;
  text: string[];
  btnText: string;
}

export interface IMatch {
  host_id: string;
  house_id: string;
  student_id: string;
  match_status?: MatchStatusEnum;
  student_check_in_date?: string;
  student_check_out_date?: string;
  offered_check_in_date?: string;
  offered_check_out_date?: string;
  check_in_date?: string;
  check_out_date?: string;
}

export type TPersonValidationErrors = Partial<{
  [K in keyof IPerson]: string;
}>;

export type TModeMatch = "acceptInvite" | "rejectInvite" | "removeMatch";

export interface IPerson {
  user_id: string;
  first_name?: string;
  last_name?: string;
  phone_code?: string;
  phone_number?: string;
  gender?: TGender;
  spoken_languages?: string[];
  default_language?: string;
  description?: string;
  privacy: number;
  user_type: RoleUser;
  not_provided_fields?: string[];
  // Student
  birth_date?: string;
  birth_country?: string;
  study?: string;
  past_experience?: TStudentPastExperience[];
  autonomy?: number;
  categorization?: TStudentCategorization[];
  propose?: TStudentPurpose[];
  ideal_match?: TStudentIdealMatch[];
  match_characteristics?: TStudentMatchCharacteristics[];
  amount_looking_people?: TStudentAmountLookingPeople;

  // Host
  source_to_hospi?: THostSourceToHospi[];
  reason_to_become_host?: THostReasonToBecomeHost[];

  // search preferences
  latitude?: number;
  longitude?: number;
  radius_in_meters?: number;
  search_location_string?: string;
  city_of_residence?: ICityOfResidence;
  max_budget?: number | null;
  min_area?: number | null;
  preferred_move_in?: string;
  preferred_move_out?: string | null;

  // Host
  requested_autonomy?: number;
  verification_status?: TVerify;
  // Service
  is_profile_complete?: boolean;
  thumbnail_url?: string;
  //email preferences
  email_preferences_room?: boolean;
  email_preferences_chat?: boolean;
  email_preferences_match?: boolean;
  email_preferences_general?: boolean;

  intake_data?: { start_time: string; end_time: string } | null;
}

export type TPersonDetails = Pick<
  IPerson,
  | "first_name"
  | "last_name"
  | "birth_date"
  | "birth_country"
  | "phone_code"
  | "thumbnail_url"
>;

export const StudentCategorizationValues = [
  "clean",
  "quiet",
  "eco_friendly",
  "animal_friendly",
  "social",
  "pro_active",
  "honest",
  "outgoing"
] as const;
export type TStudentCategorization =
  (typeof StudentCategorizationValues)[number];

export const StudentPurposeValues = [
  "study",
  "work",
  "internship",
  "other"
] as const;
export type TStudentPurpose = (typeof StudentPurposeValues)[number];

export const StudentIdealMatchValues = [
  "make_friends",
  "do_things_together",
  "have_chat",
  "own_things"
] as const;
export type TStudentIdealMatch = (typeof StudentIdealMatchValues)[number];
export const StudentMatchCharacteristicsValues = [
  "quiet",
  "city_center",
  "close_public_transport",
  "unique",
  "cosy",
  "spacious",
  "characteristic",
  "lots_greenery",
  "animal_friendly"
] as const;

export type TStudentMatchCharacteristics =
  (typeof StudentMatchCharacteristicsValues)[number];
export const StudentPastExperienceValues = [
  "Lived with host",
  "Lived abroad",
  "Lived on my own",
  "Lived with guest family",
  "None of the above"
] as const;
export type TStudentPastExperience =
  (typeof StudentPastExperienceValues)[number];

export const StudentAmountLookingPeopleValues = [
  "myself",
  "two",
  "three",
  "four",
  "more_than_five"
] as const;
export type TStudentAmountLookingPeople =
  (typeof StudentAmountLookingPeopleValues)[number];

export const HostReasonToBecomeHostValues = [
  "space",
  "help",
  "income",
  "new_people",
  "learn_culture",
  "help_with_household",
  "other"
] as const;
export type THostReasonToBecomeHost =
  (typeof HostReasonToBecomeHostValues)[number];

export const HostSourceToHospiValues = [
  "google",
  "municipality_website",
  "newspaper_or_news",
  "flyer_or_poster",
  "friend",
  "other",
  "not_remember"
] as const;
export type THostSourceToHospi = (typeof HostSourceToHospiValues)[number];

export const HousePetsValues = ["no_pets", "dogs", "cats", "other"] as const;
export type THousePets = (typeof HousePetsValues)[number];

export const HouseCharacteristicsValues = [
  "single_person",
  "couple",
  "guest_family",
  "student_house",
  "community",
  "private_house"
] as const;
export type THouseCharacteristics = (typeof HouseCharacteristicsValues)[number];

export const HouseAccommodationValues = ["furnished", "non_furnished"] as const;
export type THouseAccommodation = (typeof HouseAccommodationValues)[number];

export type THousePrivateItems = (typeof PRIVATE_USE)[number];
export type THouseSharedItems = (typeof SHARED_USE)[number];

export const HouseAmountOfPeopleValues = [
  "one",
  "two",
  "three",
  "four",
  "more_than_five"
] as const;
export type THouseAmountOfPeople = (typeof HouseAmountOfPeopleValues)[number];
export const HouseSecurityItemsValues = [
  "smoke_detector",
  "fire_extinguisher",
  "carbon_monoxide_detector",
  "locked_rent_space",
  "covered_bicycle_shed"
] as const;
export type THouseSecurityItems = (typeof HouseSecurityItemsValues)[number];
export const HouseOtherItemsAndServicesValues = [
  "wifi",
  "tv",
  "washing_machine",
  "free_parking",
  "paid_parking",
  "bike_available",
  "shared_space_cleaning",
  "private_space_cleaning",
  "bedding_and_linen"
] as const;
export type THouseOtherItemsAndServices =
  (typeof HouseOtherItemsAndServicesValues)[number];

export const HouseCommonActivityOccurrenceValues = [
  "daily",
  "weekly",
  "monthly",
  "never"
] as const;
export type THouseCommonActivityOccurrence =
  (typeof HouseCommonActivityOccurrenceValues)[number];
export const HouseTenantDescriptionValues = [
  "vegetarian",
  "no_smoking",
  "flexitarian"
] as const;
export type THouseTenantDescription =
  (typeof HouseTenantDescriptionValues)[number];

export const HouseTenantCharacteristicsValues = [
  "clean",
  "quiet",
  "eco_friendly",
  "animal_friendly",
  "social",
  "pro_active",
  "honest",
  "outgoing",
  "religious"
] as const;
export type THouseTenantCharacteristics =
  (typeof HouseTenantCharacteristicsValues)[number];

export interface IHouse {
  user_id?: string;
  house_id?: string;
  kind: THouseType | undefined;
  country: string;
  province: string;
  postal_code: string;
  house_number: string;
  additional_house_number: string;
  street: string;
  city: string;
  bedroom_area: string | null;
  house_area: string | null;
  shared_items: THouseSharedItems[];
  private_items: THousePrivateItems[];
  description: string;
  host_description?: string | null;
  available_from: string;
  available_to: string;
  price: string | null;
  additional_expenses: string | null;
  service_costs: string | null;
  deposit: string | null;
  min_tennant_age: number;
  max_tennant_age: number;
  gender: TGenderBE | null;
  spoken_languages: string[];
  latitude?: number;
  longitude?: number;
  is_published?: boolean;
  photos?: IHousePhoto[];
  verification_status?: boolean;
  is_favorited?: boolean;
  published_at?: string;
  house_status?: (typeof HOUSE_STATUS)[number];
  security_items?: THouseSecurityItems[] | null;
  other_items_and_services?: THouseOtherItemsAndServices[] | null;
  // accommodation?: TStudentMatchCharacteristics[] | null; // TODO
  household_description?: string | null;
  spend_time?: THouseCommonActivityOccurrence | null;
  help_tenant?: THouseCommonActivityOccurrence | null;
  amount_of_people?: THouseAmountOfPeople | null;
  accommodation?: THouseAccommodation | null;
  uniqueness_accommodation?: TStudentMatchCharacteristics[] | null;
  tenant_spoken_languages: string[];
  tenant_characteristics?: THouseTenantCharacteristics[] | null;
  tenant_description?: THouseTenantDescription[] | null;
  characteristics?: THouseCharacteristics | null;
  people_living?: string[] | null;
  pets?: THousePets[] | null;
}

export type TRentPeriod = {
  rent_dates: { check_in_date: string; check_out_date: string }[];
};

export type THouseAddress = Pick<
  IHouse,
  | "street"
  | "house_number"
  | "additional_house_number"
  | "postal_code"
  | "province"
  | "country"
  | "house_id"
  | "available_from"
  | "available_to"
>;

export type THostShort = Pick<
  IPerson,
  "thumbnail_url" | "first_name" | "last_name" | "user_id"
>;

export interface IStudentStripeMatches {
  host: THostShort;
  address: THouseAddress;
  period: { start: string; end: string; currently: boolean };
}

export type IUserShort = Pick<
  IPerson,
  | "user_id"
  | "user_type"
  | "thumbnail_url"
  | "first_name"
  | "last_name"
  | "gender"
  | "birth_date"
  | "birth_country"
  | "spoken_languages"
  | "description"
  | "autonomy"
  | "requested_autonomy"
  | "privacy"
  | "preferred_move_in"
  | "preferred_move_out"
  | "categorization"
  | "ideal_match"
  | "match_characteristics"
  | "search_location_string"
  | "propose"
  | "past_experience"
> & {
  active_years: number;
  house_rents_count?: number;
  hosted_tenants_count?: number;
  verification_status?: TVerify;
};

export interface IExperience {
  rented_rooms?: number;
  rented_from?: string;
  rented_to?: string;
  isChatted: boolean;
  isInvited: boolean;
  student_check_in_date?: string | null;
  student_check_out_date?: string | null;
  offered_check_in_date?: string | null;
  offered_check_out_date?: string | null;
}

export type IHouseShortInfo = Pick<
  IHouse,
  | "available_from"
  | "available_to"
  | "house_id"
  | "max_tennant_age"
  | "min_tennant_age"
  | "gender"
  | "spoken_languages"
  | "published_at"
  | "street"
  | "house_number"
  | "additional_house_number"
  | "house_status"
> &
  TRentPeriod;

export type IHouseWithRentPeriod = IHouse & TRentPeriod;
export type IHouseRepublishPayload = Pick<
  IHouse,
  "available_from" | "available_to"
>;

export type IHouseStudentStripe = Pick<
  IHouse,
  | "user_id"
  | "house_id"
  | "verification_status"
  | "available_from"
  | "available_to"
  | "street"
  | "city"
  | "bedroom_area"
  | "kind"
  | "price"
  | "is_favorited"
  | "published_at"
> & {
  photos: { file_name: string; url: string }[];
};

export interface IHostDetailsResponse {
  verification_status: TVerify | undefined;
  first_name: string;
  last_name: string;
  active_years: number;
  hosted_times: number;
  thumbnail_url: string;
  gender: TGender;
  spoken_languages: string[];
  description: string;
  autonomy: number;
  requested_autonomy: number;
}

export type TTenantsHouse = Pick<
  IHouse,
  "house_id" | "street" | "house_number" | "additional_house_number" | "kind"
>;

export interface ITenants {
  contract: Pick<
    IMatch,
    "offered_check_in_date" | "offered_check_out_date" | "match_status"
  >;
  student: IUserShort;
  house: TTenantsHouse;
  host_id: string;
}

export interface IInputData {
  readonly title?: string;
  readonly placeholder?: string;
  readonly prefix?: string | React.ReactNode | undefined;
  children?: React.ReactNode;
}

export type UserAttributesKeys = "sub" | "email_verified" | "profile" | "email";
export type StrNum = string | number;
export type DateNone = Date | undefined;

export interface ISelectItem {
  value: StrNum;
  label: string;
}

export interface IPropsFC {
  title: string;
  placeholder: string;
  w: string;
  grow: string;
}

export interface IPropsFCOnlyPlaceholder {
  placeholder: string;
  w: string;
  grow: string;
}

export interface IValidatorIn {
  readonly line: StrNum;
  params?: { [propName: string]: StrNum };
}

export interface IValidateOut {
  //   state: validatorState;
  state: boolean;
  message: string;
}

export type TMode = "create" | "edit" | "re-publish";

export interface ISearchPreferences {
  latitude: number;
  longitude: number;
  radius_in_meters: number;
  search_location_string: string;
  city_of_residence: ICityOfResidence;
  max_budget?: number;
  min_area?: number;
  preferred_move_in?: string;
  preferred_move_out?: string;
}
